import {
  getAuthSession,
  headers,
  parseSaveRequest,
  parseSubmitRequest,
  type SectionType,
} from '@/helpers';
import {
  type Application,
  type ApplicationDetailsResponseDTO,
  type ApplicationResponse,
  type DepositFundsRequestDTO,
  type DepositFundsResponseDTO,
  type DepositInfoResponse,
  type InitApplicationRequestDTO,
  type InitApplicationResponse,
  type InitiateConflictResponse,
  type MonetaryAmount,
  type NotOldEnoughResponse,
  type ProductBoundaryExceededResponse,
  type SignApplicationRequest,
  type SignApplicationResponse,
  type SubmitApplicationRequest,
  type SubmitApplicationResponse,
} from '@/types/consumer-deposit-application-service';
import { type KycSurvey } from '@/types/know-your-customer-service';

const applicationUrl = `${import.meta.env.VITE_RESURS_EKS_HOST}/api/consumer_deposit_application_service/applications`;

export const initApplication = async (
  productCode: string,
  campaignCode?: string,
) => init(productCode, campaignCode);

const init = async (
  productCode: string,
  campaignCode?: string,
): Promise<
  | InitApplicationResponse
  | InitiateConflictResponse
  | NotOldEnoughResponse
  | ProductBoundaryExceededResponse
> => {
  const { accessToken } = getAuthSession();

  const body: InitApplicationRequestDTO = {
    campaignCode,
    productCode,
  };

  return fetch(applicationUrl, {
    body: JSON.stringify(body),
    headers: headers(accessToken),
    method: 'POST',
  }).then(async (response) => {
    if (response.status !== 201) {
      if (response.status == 409) {
        console.warn('Application already exists');

        return response.json().then((json: InitiateConflictResponse) => json);
      } else if (response.status == 422) {
        console.warn('Product boundary exceeded');

        return response
          .json()
          .then((json: ProductBoundaryExceededResponse) => json);
      } else if (response.status === 451) {
        return response.json().then((json: NotOldEnoughResponse) => json);
      } else {
        const res = await response.json();

        throw Error(`Could not initiate application ${JSON.stringify(res)}`);
      }
    } else {
      return response.json().then((json: InitApplicationResponse) => json);
    }
  });
};

export const fetchApplication = async (applicationId: string) =>
  get(applicationId);

const get = async (applicationId: string): Promise<ApplicationResponse> => {
  const { accessToken } = getAuthSession();

  return fetch(`${applicationUrl}/${applicationId}`, {
    headers: headers(accessToken),
  }).then(async (response) => {
    if (response.status !== 200) {
      const res = await response.json();

      throw Error(`Could not initiate application ${JSON.stringify(res)}`);
    } else {
      return response.json().then((json) => {
        return {
          _links: json._links,
          application: json.application,
          kycSurvey: json.kycSurvey,
          kycVersion: json.kycVersion,
        };
      });
    }
  });
};

export const submitApplication = async (
  url: string,
  onCancelledUri: string,
  onSuccessUri: string,
  application: ApplicationDetailsResponseDTO,
  kycSurvey: KycSurvey,
  sectionMap: SectionType[],
) =>
  submit(
    url,
    parseSubmitRequest(
      onCancelledUri,
      onSuccessUri,
      kycSurvey,
      application,
      sectionMap,
    ),
  );

const submit = async (
  url: string,
  request: SubmitApplicationRequest,
): Promise<SubmitApplicationResponse> => {
  const { accessToken } = getAuthSession();

  return fetch(url, {
    body: JSON.stringify(request),
    headers: headers(accessToken),
    method: 'POST',
  }).then(async (response) => {
    const body = await response.json();

    if (response.status == 200 && body) {
      return body;
    } else {
      console.error('Something went wrong', body);
      throw new Error('Something went wrong');
    }
  });
};

export const resetApplication = async (applicationId: string) =>
  reset(applicationId);

const reset = async (
  applicationId: string,
): Promise<InitApplicationResponse | InitiateConflictResponse> => {
  const { accessToken } = getAuthSession();

  return fetch(`${applicationUrl}/${applicationId}/reset`, {
    headers: headers(accessToken),
    method: 'PUT',
  }).then(async (response) => {
    if (response.status === 200) {
      return response.json().then((json: InitApplicationResponse) => json);
    } else {
      const res = await response.json();

      throw Error(`Could not reset application ${JSON.stringify(res)}`);
    }
  });
};

export const signApplication = async (
  applicationId: string,
  onSuccessUri: string,
  onCancelledUri: string,
) => sign(applicationId, { onCancelledUri, onSuccessUri });

const sign = async (
  applicationId: string,
  request: SignApplicationRequest,
): Promise<SignApplicationResponse> => {
  const { accessToken } = getAuthSession();

  return fetch(`${applicationUrl}/${applicationId}/sign`, {
    body: JSON.stringify(request),
    headers: headers(accessToken),
    method: 'POST',
  }).then(async (response) => {
    const body = await response.json();

    if (response.status == 200) {
      return body;
    } else {
      console.error('Something went wrong', body);
      throw new Error('Something went wrong');
    }
  });
};

export const saveApplication = async (
  applicationId: string,
  request: ApplicationDetailsResponseDTO,
): Promise<boolean> => save(applicationId, parseSaveRequest(request));

const save = async (
  applicationId: string,
  request: Application,
): Promise<boolean> => {
  const { accessToken } = getAuthSession();

  return fetch(`${applicationUrl}/${applicationId}`, {
    body: JSON.stringify(request),
    headers: headers(accessToken),
    method: 'put',
  }).then(async (response) => {
    if (response.status === 200) {
      return true;
    } else {
      response
        .json()
        .then((body) => console.error('Something went wrong', body));

      return false;
    }
  });
};

export const pollForAccountInfo = async (
  applicationId: string,
): Promise<DepositInfoResponse | undefined> => poll(applicationId);

const poll = async (
  applicationId: string,
): Promise<DepositInfoResponse | undefined> => {
  const { accessToken } = getAuthSession();
  const maxRetries = 6;
  let result: DepositInfoResponse | undefined = undefined;
  const retryTime = 5000;
  let attempt = 0;
  const url = `${applicationUrl}/${applicationId}/deposit-information`;

  while (attempt < maxRetries && !result) {
    attempt++;
    try {
      await fetch(url, {
        headers: headers(accessToken),
      }).then(async (response) => {
        if (response.status === 200) {
          result = (await response.json()) as DepositInfoResponse;
        } else {
          await new Promise((resolve) => setTimeout(resolve, retryTime));
        }
      });
    } catch (error) {
      console.error(error);
      if (attempt < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, retryTime));
      }
    }
  }

  if (!result) {
    throw new Error('Could not fetch account information');
  }

  return result;
};

export const depositFunds = async (
  applicationId: string,
  redirectUrl: string,
  amount: MonetaryAmount,
  transactionText?: string,
) => postDepositFunds(applicationId, redirectUrl, amount, transactionText);

const postDepositFunds = async (
  applicationId: string,
  redirectUrl: string,
  amount: MonetaryAmount,
  transactionText?: string,
): Promise<DepositFundsResponseDTO> => {
  const { accessToken } = getAuthSession();
  const body: DepositFundsRequestDTO = {
    amount,
    context: {
      redirectUrl,
    },
    transactionText,
  };

  return fetch(`${applicationUrl}/${applicationId}/deposit_funds`, {
    body: JSON.stringify(body),
    headers: headers(accessToken),
    method: 'POST',
  }).then(async (response) => {
    if (response.status !== 200) {
      throw Error(`Could not deposit funds ${JSON.stringify(response)}`);
    } else {
      return response.json().then((json: DepositFundsResponseDTO) => json);
    }
  });
};
